import { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react"
import { useUpdateSection, useDeleteSection } from "@/lib/queries"
import { registerSectionFlush, unregisterSectionFlush } from "@/lib/sectionSaveRegistry"
import { TiptapEditor } from "./TiptapEditor"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Trash2, ChevronUp, ChevronDown, Loader2, Check, X } from "lucide-react"
import { ApiError } from "@/lib/api"

interface SectionEditorProps {
  section: {
    id: number
    heading: string
    body_html: string
    lock_version: number
  }
  sectionKey?: string
  isWorkflow: boolean
  showCitationButton?: boolean
  isFirst: boolean
  isLast: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onUpdate: (updatedSection: {
    id: number
    heading: string
    body_html: string
    lock_version: number
  }) => void
  onDelete: () => void
  canEdit?: boolean
}

type SaveStatus = "idle" | "saving" | "saved" | "error"

export function SectionEditor({
  section,
  sectionKey,
  isWorkflow,
  showCitationButton,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onUpdate,
  onDelete,
  canEdit = true,
}: SectionEditorProps) {
  const [heading, setHeading] = useState(section.heading)
  const [bodyHtml, setBodyHtml] = useState(section.body_html)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle")
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [lockVersion, setLockVersion] = useState(section.lock_version)

  const updateSection = useUpdateSection()
  const deleteSection = useDeleteSection()

  const headingRef = useRef<HTMLDivElement>(null)
  const [headingHeight, setHeadingHeight] = useState(0)

  useLayoutEffect(() => {
    const el = headingRef.current
    if (!el) return
    const update = () => setHeadingHeight(el.offsetHeight)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isSavingRef = useRef(false)
  const needsResaveRef = useRef(false)
  const lastSaveRef = useRef<{
    heading: string
    body_html: string
  }>({
    heading: section.heading,
    body_html: section.body_html,
  })

  // Refs that always hold the latest values — used by saveSection so that
  // even a re-save triggered from the `finally` block reads current state.
  const currentValuesRef = useRef({ heading, bodyHtml, lockVersion })
  currentValuesRef.current = { heading, bodyHtml, lockVersion }

  // Only sync from props when the section ID changes (initial mount or different section),
  // NOT when the prop updates from our own save response — that would overwrite local edits.
  const sectionIdRef = useRef(section.id)
  useEffect(() => {
    if (section.id !== sectionIdRef.current) {
      sectionIdRef.current = section.id
      setHeading(section.heading)
      setBodyHtml(section.body_html)
      setLockVersion(section.lock_version)
      lastSaveRef.current = {
        heading: section.heading,
        body_html: section.body_html,
      }
    }
  }, [section])

  const saveSection = useCallback(async () => {
    const { heading: curHeading, bodyHtml: curBodyHtml, lockVersion: curLockVersion } =
      currentValuesRef.current

    const hasChanges =
      curHeading !== lastSaveRef.current.heading ||
      curBodyHtml !== lastSaveRef.current.body_html

    if (!hasChanges) {
      setSaveStatus("idle")
      return
    }

    // Serialize saves: if one is already in flight, flag for re-save when it completes
    if (isSavingRef.current) {
      needsResaveRef.current = true
      return
    }

    isSavingRef.current = true
    setSaveStatus("saving")
    setErrorMessage("")

    // Capture what we're sending so we can update lastSaveRef accurately
    const sentHeading = curHeading
    const sentBodyHtml = curBodyHtml

    try {
      const updated = await updateSection.mutateAsync({
        id: section.id,
        heading: sentHeading,
        body_html: sentBodyHtml,
        lock_version: curLockVersion,
      })

      lastSaveRef.current = { heading: sentHeading, body_html: sentBodyHtml }
      setLockVersion(updated.lock_version)
      setSaveStatus("saved")

      onUpdate({
        id: updated.id,
        heading: updated.heading,
        body_html: updated.body_html,
        lock_version: updated.lock_version,
      })

      setTimeout(() => setSaveStatus("idle"), 2000)
    } catch (error) {
      setSaveStatus("error")
      if (error instanceof ApiError && error.status === 409) {
        setErrorMessage("Conflict: Section was modified elsewhere. Please refresh.")
      } else {
        setErrorMessage("Failed to save. Please try again.")
      }
    } finally {
      isSavingRef.current = false

      // If edits happened while we were saving, trigger another save with latest values
      if (needsResaveRef.current) {
        needsResaveRef.current = false
        saveSection()
      }
    }
  }, [section.id, updateSection, onUpdate])

  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveSection()
    }, 2000)

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [heading, bodyHtml, saveSection])

  const handleBlur = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    saveSection()
  }

  const saveSectionRef = useRef(saveSection)
  saveSectionRef.current = saveSection

  useEffect(() => {
    const flush = async () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }
      await saveSectionRef.current()
      while (isSavingRef.current || needsResaveRef.current) {
        await new Promise((r) => setTimeout(r, 50))
      }
    }
    registerSectionFlush(section.id, flush)
    return () => unregisterSectionFlush(section.id)
  }, [section.id])

  const handleDelete = async () => {
    try {
      await deleteSection.mutateAsync(section.id)
      onDelete()
    } catch (error) {
      console.error("Failed to delete section:", error)
    }
  }

  const SaveStatusIndicator = () => {
    switch (saveStatus) {
      case "saving":
        return (
          <span className="flex items-center text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Saving...
          </span>
        )
      case "saved":
        return (
          <span className="flex items-center text-xs text-lime-600">
            <Check className="h-3 w-3 mr-1" />
            Saved
          </span>
        )
      case "error":
        return (
          <span className="flex items-center text-xs text-destructive">
            <X className="h-3 w-3 mr-1" />
            {errorMessage || "Error"}
          </span>
        )
      default:
        return null
    }
  }

  if (!canEdit) {
    return (
      <div id={sectionKey ? `section-${sectionKey}` : undefined} className="border rounded-lg bg-card">
        <div className="p-4 border-b rounded-t-lg">
          <h3 className="font-semibold">{heading}</h3>
        </div>
        <div
          className="p-4 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      </div>
    )
  }

  return (
    <div id={sectionKey ? `section-${sectionKey}` : undefined} className="border rounded-lg bg-card">
      <div ref={headingRef} className="sticky top-0 z-20 bg-card border-b rounded-t-lg p-4 pb-3">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Input
                value={heading}
                onChange={(e) => setHeading(e.target.value)}
                onBlur={handleBlur}
                placeholder="Section heading"
                className="font-semibold"
              />
              <SaveStatusIndicator />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              <Button
                size="icon"
                variant="ghost"
                onClick={onMoveUp}
                disabled={isFirst}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={onMoveDown}
                disabled={isLast}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="icon" variant="ghost">
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Section</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this section? This action
                      cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 pt-2">
        <TiptapEditor
          content={bodyHtml}
          onChange={(html) => setBodyHtml(html)}
          isWorkflow={isWorkflow}
          showCitationButton={showCitationButton}
          toolbarStickyTop={headingHeight}
        />
      </div>
    </div>
  )
}
